import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { quickSweep } from '../api'
import { price as fiyat, qty } from '../format'
import type { Side } from '../types'

/**
 * Defterde bir fiyata tıklayınca imlecin yanında açılan "Hızlı Al / Hızlı Sat"
 * balonu: lot girişi + tek düğme. Tıklanan fiyata KADAR karşı taraftan girilen
 * lot kadarını süpürür (`POST /api/orders/sweep`); giriş, görünen mevcutla
 * ön-dolu gelir — hepsini vurmak zorunda değilsin (kullanıcı isteği 2026-07-29).
 *
 * Gösterilen mevcut yalnız ÖNİZLEMEDİR — üst sınırı sunucu, motor kilidi
 * altında hesaplar ve isteği mevcutla SINIRLAR (istemcinin gördüğü 1 sn bayat
 * olabilir). Bu yüzden emir daima tamamen dolar, artık pasif emir kalmaz.
 *
 * `OrderEditPopover` ile aynı desen: body'ye portal (defter `overflow: hidden`
 * kırpmasın), dışarı tıklama/Esc = vazgeç, ekran kenarına kenetlenir.
 */

interface Props {
  contract: string
  /** Süpürme emrinin yönü: satış seviyesine tıklandıysa BUY (Hızlı Al). */
  side: Side
  /** Tıklanan seviyenin fiyatı — süpürmenin limit fiyatı. */
  price: number
  /** Önizleme: bu fiyata kadar süpürülebilir lot (kümülatif `available`). */
  lots: number
  /** İmlecin tıklama anındaki ekran konumu. */
  at: { x: number; y: number }
  onClose: () => void
}

const GENISLIK = 190

export function QuickSweepPopover({ contract, side, price, lots, at, onClose }: Props) {
  // Lot girişi görünen mevcutla ön-dolu — dokunmadan onaylarsan hepsini alır.
  const [lot, setLot] = useState(String(Math.max(1, Math.floor(lots))))
  const [hata, setHata] = useState<string | null>(null)
  const [gonderiliyor, setGonderiliyor] = useState(false)
  const kutuRef = useRef<HTMLDivElement>(null)
  const [konum, setKonum] = useState({ top: at.y + 8, left: at.x + 8 })

  // İmlecin yanında dur ama ekran dışına taşma.
  useLayoutEffect(() => {
    const yukseklik = kutuRef.current?.offsetHeight ?? 0
    const left = Math.max(8, Math.min(at.x + 8, window.innerWidth - GENISLIK - 8))
    const altaSigar = at.y + 8 + yukseklik <= window.innerHeight - 8
    const top = altaSigar ? at.y + 8 : Math.max(8, at.y - yukseklik - 8)
    setKonum({ top, left })
  }, [at, hata])

  // Dışarı tıklama = vazgeç. Balonu açan tıklamanın mousedown'ı çoktan geçti.
  useEffect(() => {
    const disariTikla = (event: MouseEvent) => {
      if (kutuRef.current && !kutuRef.current.contains(event.target as Node)) onClose()
    }
    const escBas = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', disariTikla)
    window.addEventListener('keydown', escBas)
    return () => {
      document.removeEventListener('mousedown', disariTikla)
      window.removeEventListener('keydown', escBas)
    }
  }, [onClose])

  const istenen = Number(lot)
  const gecerli = Number.isInteger(istenen) && istenen >= 1

  async function supur() {
    if (!gecerli || gonderiliyor) return
    setHata(null)
    setGonderiliyor(true)
    try {
      await quickSweep({ contract, side, price, quantity: istenen })
      onClose()
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Süpürme başarısız')
      setGonderiliyor(false)
    }
  }

  const etiket = side === 'BUY' ? 'Hızlı Al' : 'Hızlı Sat'

  return createPortal(
    <div
      ref={kutuRef}
      className="sweep-pop"
      style={{ top: konum.top, left: konum.left, width: GENISLIK }}
      role="dialog"
      aria-label={etiket}
    >
      <div className="sweep-lot">
        <input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={lot}
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setLot(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') supur()
          }}
          aria-label="Süpürülecek lot"
        />
        <span className="sweep-lot-unit">lot</span>
      </div>
      <button
        type="button"
        className={`sweep-btn ${side === 'BUY' ? 'buy' : 'sell'}`}
        disabled={gonderiliyor || !gecerli}
        title={`${fiyat(price)} fiyatına kadar karşı taraftan ${gecerli ? istenen : '?'} lot süpür`}
        onClick={supur}
      >
        {gonderiliyor ? 'Süpürülüyor…' : gecerli ? `${etiket} · ${istenen} lot` : etiket}
      </button>
      <div className="sweep-hint">
        {fiyat(price)} ve daha iyisi · mevcut ~{qty(lots)} lot — fazlası mevcutla sınırlanır
      </div>
      {hata && <div className="sweep-error" role="alert">{hata}</div>}
    </div>,
    document.body,
  )
}
